import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { walletAPI } from '../../services/api';
import { 
  Wallet, Plus, CreditCard, ArrowUpRight, ArrowDownRight, 
  CheckCircle, AlertCircle, Loader2, Receipt, Gift, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const WalletPage = () => {
  const { user, refreshBalance } = useAuth();
  const { t, locale } = useI18n();
  const [searchParams] = useSearchParams();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditPackages, setCreditPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [availableMethods, setAvailableMethods] = useState([]);

  useEffect(() => {
    fetchTransactions();
    fetchPaymentMethods();
    fetchCreditPackages();

    const mollieId = searchParams.get('mollie_id');
    if (mollieId) {
      checkPaymentStatus(mollieId);
    }
    
    const paypalSuccess = searchParams.get('paypal_success');
    const paypalOrderId = searchParams.get('token');
    if (paypalSuccess === 'true' && paypalOrderId) {
      capturePayPalOrder(paypalOrderId);
    }
  }, [searchParams]);

  const fetchCreditPackages = async () => {
    try {
      const res = await walletAPI.getCreditPackages();
      setCreditPackages(res.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchPaymentMethods = async () => {
    try {
      const res = await walletAPI.getPaymentMethods();
      const methods = res.data || [];
      setAvailableMethods(methods);
      if (methods.length > 0 && !paymentMethod) {
        setPaymentMethod(methods[0].id);
      }
    } catch (err) { console.error(err); }
  };

  const capturePayPalOrder = async (orderId) => {
    setProcessing(true);
    try {
      const response = await walletAPI.capturePayPal(orderId);
      if (response.data.status === 'completed') {
        setPaymentStatus({
          type: 'success',
          message: `${formatCurrency(response.data.amount)} erfolgreich via PayPal aufgeladen!`
        });
        await refreshBalance();
        await fetchTransactions();
      }
    } catch (error) {
      setPaymentStatus({ type: 'error', message: 'PayPal Zahlung fehlgeschlagen' });
    } finally {
      setProcessing(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await walletAPI.getTransactions({ limit: 50 });
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await walletAPI.checkPaymentStatus(sessionId);
      if (response.data.status === 'completed') {
        setPaymentStatus({
          type: 'success',
          message: `${formatCurrency(response.data.amount)} erfolgreich aufgeladen!`
        });
        await refreshBalance();
        await fetchTransactions();
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    }
  };

  const getTopupAmount = () => {
    if (customAmount) return parseFloat(customAmount);
    if (selectedPackage) return selectedPackage.price_eur;
    return 0;
  };

  const getTopupCredits = () => {
    if (selectedPackage) return selectedPackage.credits + (selectedPackage.bonus_credits || 0);
    return 0;
  };

  const handleTopup = async () => {
    const amount = getTopupAmount();
    
    if (amount < 10) {
      setPaymentStatus({ type: 'error', message: 'Mindestbetrag ist 10 EUR' });
      return;
    }

    setProcessing(true);
    setPaymentStatus(null);

    try {
      const response = await walletAPI.createTopup(amount, paymentMethod);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      setPaymentStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Zahlung fehlgeschlagen'
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(locale, {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="wallet-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{t('wallet.title')}</h1>
          <p className="text-slate-400 mt-1">Verwalten Sie Ihr Guthaben und kaufen Sie Credit-Pakete</p>
        </div>

        {paymentStatus && (
          <div className={`p-4 rounded-xl flex items-start gap-3 ${
            paymentStatus.type === 'success' 
              ? 'bg-emerald-500/10 border border-emerald-500/20' 
              : 'bg-red-500/10 border border-red-500/20'
          }`} data-testid="payment-status-alert">
            {paymentStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            )}
            <p className={paymentStatus.type === 'success' ? 'text-emerald-400' : 'text-red-400'}>
              {paymentStatus.message}
            </p>
          </div>
        )}

        {/* Balance Overview */}
        <div className="glass-card p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">{t('wallet.currentBalance')}</p>
              <p className="text-4xl font-heading font-bold text-white mt-1">
                {(user?.balance || 0).toFixed(1)} <span className="text-lg text-slate-400">Credits</span>
              </p>
            </div>
            <div className="w-16 h-16 bg-[#EC2229]/10 rounded-2xl flex items-center justify-center">
              <Wallet className="w-8 h-8 text-[#EC2229]" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Credit Packages */}
            {creditPackages.length > 0 && (
              <div className="glass-card p-6">
                <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#EC2229]" />
                  Credit-Pakete
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {creditPackages.map(pkg => {
                    const isSelected = selectedPackage?.id === pkg.id && !customAmount;
                    return (
                      <button
                        key={pkg.id}
                        onClick={() => { setSelectedPackage(pkg); setCustomAmount(''); }}
                        className={`relative p-5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-2 ring-[#EC2229]/30'
                            : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'
                        }`}
                        data-testid={`package-${pkg.id}`}
                      >
                        {pkg.bonus_credits > 0 && (
                          <div className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Gift className="w-3 h-3" /> +{pkg.bonus_credits} Bonus
                          </div>
                        )}
                        <p className="text-white font-semibold text-base">{pkg.name}</p>
                        <div className="flex items-baseline gap-2 mt-2">
                          <span className="text-2xl font-heading font-bold text-[#EC2229]">{pkg.credits}</span>
                          <span className="text-sm text-slate-400">Credits</span>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-lg font-bold text-white">{formatCurrency(pkg.price_eur)}</span>
                          {pkg.bonus_credits > 0 && (
                            <span className="text-xs text-emerald-400">= {pkg.credits + pkg.bonus_credits} Credits total</span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-500 mt-1">
                          {formatCurrency(pkg.price_eur / (pkg.credits + (pkg.bonus_credits || 0)))} / Credit
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Amount */}
            <div className="glass-card p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4">Eigener Betrag</h2>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">EUR</span>
                <Input
                  type="number"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setSelectedPackage(null); }}
                  placeholder="Betrag eingeben (min. 10 EUR)"
                  className="input-field pl-14"
                  min="10"
                  data-testid="custom-amount"
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="glass-card p-6">
              <Label className="label-text mb-3 block">{t('wallet.paymentMethod')}</Label>
              {availableMethods.length === 0 ? (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10 text-center">
                  <p className="text-slate-400 text-sm">Keine Zahlungsmethoden verfügbar.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {availableMethods.map(method => (
                    <button
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`p-4 rounded-xl border flex items-center gap-3 transition-all ${
                        paymentMethod === method.id
                          ? 'bg-[#EC2229]/10 border-[#EC2229]/50 ring-2 ring-[#EC2229]/30'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                      data-testid={`payment-${method.id}`}
                    >
                      <CreditCard className="w-5 h-5 text-[#EC2229]" />
                      <span className="text-white font-medium">{method.name}</span>
                    </button>
                  ))}
                </div>
              )}

              <Button
                onClick={handleTopup}
                disabled={processing || (!selectedPackage && !customAmount)}
                className="btn-primary w-full mt-4"
                data-testid="topup-submit"
              >
                {processing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird verarbeitet...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" />
                    {selectedPackage && !customAmount
                      ? `${selectedPackage.name} kaufen — ${formatCurrency(selectedPackage.price_eur)}`
                      : customAmount
                        ? `${formatCurrency(parseFloat(customAmount) || 0)} aufladen`
                        : 'Paket oder Betrag wählen'
                    }
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Selected Package Info */}
            {selectedPackage && !customAmount && (
              <div className="glass-card p-6 border-l-4 border-[#EC2229]" data-testid="selected-package-info">
                <h3 className="font-heading text-base font-semibold text-white mb-3">Ausgewähltes Paket</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Paket</span><span className="text-white font-medium">{selectedPackage.name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Credits</span><span className="text-white font-mono">{selectedPackage.credits}</span></div>
                  {selectedPackage.bonus_credits > 0 && (
                    <div className="flex justify-between"><span className="text-emerald-400">Bonus</span><span className="text-emerald-400 font-mono">+{selectedPackage.bonus_credits}</span></div>
                  )}
                  <div className="pt-2 border-t border-white/[0.06] flex justify-between"><span className="text-slate-400">Gesamt Credits</span><span className="text-[#EC2229] font-bold font-mono">{getTopupCredits()}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Preis</span><span className="text-white font-bold">{formatCurrency(selectedPackage.price_eur)}</span></div>
                </div>
              </div>
            )}

            <div className="glass-card p-6">
              <h3 className="font-heading text-base font-semibold text-white mb-4">Zahlungsinfo</h3>
              <div className="space-y-3 text-sm">
                <div className="p-3 bg-white/5 rounded-lg">
                  <p className="text-slate-400">Mindestbetrag</p>
                  <p className="text-white font-medium">10,00 EUR</p>
                </div>
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-emerald-500 font-medium flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" /> Sofortige Gutschrift
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions History */}
        <div className="glass-card p-6">
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            {t('wallet.history')}
          </h2>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-[#EC2229] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Noch keine Transaktionen</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-slate-500">Datum</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-slate-500">Beschreibung</th>
                    <th className="text-right p-3 text-xs font-bold uppercase tracking-wider text-slate-500">Betrag</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => (
                    <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`transaction-${tx.id}`}>
                      <td className="p-3 text-sm text-slate-400">{formatDate(tx.created_at)}</td>
                      <td className="p-3">
                        <p className="text-white">{tx.description}</p>
                        {tx.reference && <p className="text-xs text-slate-500 font-mono">{tx.reference}</p>}
                      </td>
                      <td className="p-3 text-right">
                        <div className={`inline-flex items-center gap-1 ${
                          tx.type === 'credit' || tx.type === 'admin_credit' || tx.type === 'bonus' ? 'text-emerald-500' : 'text-red-400'
                        }`}>
                          {tx.type === 'credit' || tx.type === 'admin_credit' || tx.type === 'bonus' ? (
                            <ArrowUpRight className="w-4 h-4" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                          <span className="font-mono font-medium">
                            {tx.amount > 0 && tx.type !== 'debit' ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default WalletPage;
